import {SolutionProductsResponse} from 'api/dataTypes';
import {
    normalize,
    prepareInitialState,
} from 'modules/ProductsGrid/containers/ProductsGridContainer/utils';
import {ProductGridItemData} from 'modules/ProductsGrid/lib/types';

/** Состояние группы товаров */
export type ProductsGridGroupState = {
    /** Идентификатор группы */
    id: number;
    /** Идентификатор родительской группы */
    parentId?: string;
    /** Название группы */
    name: string;
    /** Список идентификаторов товаров в группе */
    products: number[];
    /** Список идентификаторов дополнительных товаров в группе */
    additionalProducts: number[];
};

export type ProductsGridState = {
    productIds: number[];
    groupsIds: number[];
    products: Record<number, ProductGridItemData>;
    groups: Record<number, ProductsGridGroupState>;
};

type Action =
    | {type: 'setInitialState'; payload: SolutionProductsResponse}
    | {
          type: 'setDeleted';
          payload: {productId: number; deleted: boolean; groupId?: number};
      }
    | {type: 'setComplectsAmount'; payload: {count: number}}
    | {type: 'setProductCount'; payload: {productId: number; count: number}};

export const productsGridInitialState: ProductsGridState = {
    productIds: [],
    groupsIds: [],
    products: {},
    groups: {},
};

export const productsGridReducer = (
    state: ProductsGridState,
    action: Action,
): ProductsGridState => {
    switch (action.type) {
        case 'setDeleted': {
            const {productId, deleted, groupId} = action.payload;

            const newStateProducts = {
                ...state.products,
                [productId]: {
                    ...state.products[productId],
                    deleted,
                },
            };

            const group =
                typeof groupId !== 'undefined'
                    ? state.groups[groupId]
                    : undefined;
            // Если товар в группе помечается удаленным, то его нужно отобразить в отдельном блоке "Дополнительные товары".
            if (group) {
                // Если товар помечается удаленным, то из основного списка удаляем его.
                // И наоборот, если товар восстанавливается, то добавляем его в основной список.
                const products = deleted
                    ? group.products.filter((id) => id !== productId)
                    : [...group.products, productId];

                // Если товар помечается удаленным, то добавляем его в список дополнительных товаров
                // И наоборот, если товар восстанавливается, то удаляем его из списка дополнительных товаров.
                const additionalProducts = deleted
                    ? [...group.additionalProducts, productId]
                    : group.additionalProducts.filter((id) => id !== productId);

                return {
                    ...state,
                    products: newStateProducts,
                    groups: {
                        ...state.groups,
                        [group.id]: {
                            ...group,
                            products,
                            additionalProducts,
                        },
                    },
                };
            }

            return {
                ...state,
                products: newStateProducts,
            };
        }
        case 'setProductCount': {
            const {productId, count} = action.payload;
            return {
                ...state,
                products: {
                    ...state.products,
                    [productId]: {
                        ...state.products[productId],
                        count: count,
                    },
                },
            };
        }
        case 'setComplectsAmount': {
            const {count: complectsAmount} = action.payload;
            const allProducts = Object.values(state.products);
            const newProducts = allProducts.map((product) => ({
                ...product,
                count: complectsAmount * product.counter.addBy,
            }));
            return {
                ...state,
                products: normalize(newProducts),
            };
        }
        case 'setInitialState': {
            const rawData = action.payload.data;
            return {
                ...state,
                ...prepareInitialState(rawData),
            };
        }
    }
};
